// Typed API client for the affiliate partner-application admin BFF
// surface. Bound to BE routes under
// /v2/admin/bff/affiliate/partners/applications/* (gao-id-worker Phase 5).
//
// Risk model (matches the affiliate withdrawal-approve convention):
//   * list + detail are read (session + trusted-device gated on the BE).
//   * approve / reject / suspend are `low_config` on the BE — they flip
//     identity_affiliate_index.partner_approved and (un)lock the
//     partner-cash withdrawal flow. They REQUIRE an approved multisign
//     proposalId. This admin surface does NOT create proposals: the
//     operator creates + approves a low_config proposal out of band and
//     pastes the approved proposalId here. The browser never calls the
//     owner-scope propose endpoint (it is HMAC service-auth, not the
//     browser session), and never puts a secret in the browser.

import { apiCall } from "./client";

export type PartnerApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "suspended";

export type PartnerApplicationStatusFilter = PartnerApplicationStatus | "all";

// Mirror of the BE operator view (`toOperatorView`). Every field except
// `id` + `status` is nullable so a partial row still renders.
export interface PartnerApplicationRow {
  id: string;
  identityId: string | null;
  applicantWalletAddress: string | null;
  status: PartnerApplicationStatus;
  applicantNote: string | null;
  reviewNote: string | null;
  reviewedByOwnerId: string | null;
  reviewedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PartnerApplicationsListResponse {
  items: PartnerApplicationRow[];
}

export interface PartnerApplicationActionResponse {
  id: string;
  status: PartnerApplicationStatus;
  reviewedAt: string;
}

export type PartnerProposalAction = "approve" | "reject" | "suspend";

export interface PartnerProposalResponse {
  proposalId: string;
  actionType: "low_config";
  action: PartnerProposalAction;
  applicationId: string;
  status: string;
}

// ─── List + detail ─────────────────────────────────────────────────

export interface ListPartnerApplicationsInput {
  status?: PartnerApplicationStatusFilter;
  limit?: number;
}

export async function listPartnerApplications(
  input: ListPartnerApplicationsInput = {},
): Promise<PartnerApplicationsListResponse> {
  const query: Record<string, string | undefined> = {};
  if (input.status && input.status !== "all") query["status"] = input.status;
  if (typeof input.limit === "number") query["limit"] = String(input.limit);
  return apiCall<PartnerApplicationsListResponse>(
    "/v2/admin/bff/affiliate/partners/applications",
    { query },
  );
}

export async function getPartnerApplication(
  id: string,
): Promise<PartnerApplicationRow> {
  return apiCall<PartnerApplicationRow>(
    `/v2/admin/bff/affiliate/partners/applications/${encodeURIComponent(id)}`,
  );
}

// ─── Create the low_config proposal (browser-session, no HMAC) ──────
//
// Server-side creates the low_config proposal the mutations require. The
// browser supplies only action (+ reason); the BE binds the authoritative
// applicationId / identityId. This is NOT the owner-scope HMAC propose
// endpoint — it is the session-gated BFF route. The returned proposal is
// in `proposed` state; the operator approves it via /approvals before the
// matching mutation can use the proposalId.

export async function createPartnerApplicationProposal(
  id: string,
  input: { action: PartnerProposalAction; reason?: string; ttlSec?: number },
): Promise<PartnerProposalResponse> {
  const body: Record<string, unknown> = { action: input.action };
  if (typeof input.reason === "string" && input.reason.length > 0) body["reason"] = input.reason;
  if (typeof input.ttlSec === "number") body["ttlSec"] = input.ttlSec;
  return apiCall<PartnerProposalResponse>(
    `/v2/admin/bff/affiliate/partners/applications/${encodeURIComponent(id)}/proposals`,
    { method: "POST", body },
  );
}

// ─── Mutations (low_config — require an approved proposalId) ─────────

export async function approvePartnerApplication(
  id: string,
  input: { proposalId: string },
): Promise<PartnerApplicationActionResponse> {
  return apiCall<PartnerApplicationActionResponse>(
    `/v2/admin/bff/affiliate/partners/applications/${encodeURIComponent(id)}/approve`,
    { method: "POST", body: { proposalId: input.proposalId } },
  );
}

export async function rejectPartnerApplication(
  id: string,
  input: { proposalId: string; reason: string },
): Promise<PartnerApplicationActionResponse> {
  return apiCall<PartnerApplicationActionResponse>(
    `/v2/admin/bff/affiliate/partners/applications/${encodeURIComponent(id)}/reject`,
    { method: "POST", body: { proposalId: input.proposalId, reason: input.reason } },
  );
}

export async function suspendPartnerApplication(
  id: string,
  input: { proposalId: string; reason: string },
): Promise<PartnerApplicationActionResponse> {
  return apiCall<PartnerApplicationActionResponse>(
    `/v2/admin/bff/affiliate/partners/applications/${encodeURIComponent(id)}/suspend`,
    { method: "POST", body: { proposalId: input.proposalId, reason: input.reason } },
  );
}
